<template>
  <v-container
    fluid
    class="content fill-height"
  >
    <v-row
      no-gutters
      justify="space-between"
      class="content__row"
    >
      <v-col
        md="7"
        sm="100%"
        class="pa-5 content__col-result"
      >
        <v-card
          v-if="currentDocId"
          flat
          class="fill-height content__col-result__card"
        >
          <div
            v-for="(result, index) in currentDoc.signatureResults"
            :key="result.id"
            :style="{ 'color': appColor.textDark }"
          >
            <div
              v-if="currentDoc.signatureResults.length > 1"
              class="content__signature-title"
            >
              <h3 class="content__signature-title__text">
                Электронная подпись №{{ index + 1 }}
              </h3>
            </div>

            <v-expansion-panels
              v-model="panelStates[index]"
              flat
              multiple
              class="mb-8"
            >
              <ResultTable
                :data-arr="result.info.main"
                title="Основная информация"
              />
              <ResultTable
                :data-arr="result.info.signature"
                title="Дополнительная информация о подписи"
              />
              <ResultTable
                :data-arr="result.info.certificate"
                title="Информация о сертификате"
                :download-arr="result.download"
              />
              <ResultTable
                v-for="certificate in result.info.additionalCertificates"
                :key="certificate.id"
                :data-arr="certificate.info"
                :title="certificate.title"
                :download-arr="certificate.download"
              />
              <ResultTable
                v-for="plugin in result.info.plugins"
                :key="`${plugin.id}-${plugin.title}`"
                :data-arr="plugin.data"
                :title="plugin.title"
              />
            </v-expansion-panels>
          </div>
        </v-card>
        <v-card
          v-else
          flat
          class="pa-7 fill-height d-flex align-center justify-center"
        >
          <p class="text-center mb-0">
            Для этого файла результаты отсутствуют
          </p>
        </v-card>
      </v-col>
      <v-col
        md="5"
        sm="100%"
        class="fill-height pa-5 content__col-doclist"
      >
        <ResultFileList
          :data="mappedData"
          :selected-id="currentDocId"
          :loading="docListLoadingId"
          list-title="Список подписей"
          @documentClick="onDocClick"
          @showPreview="onShowPreview"
          @signatureBack="goBack"
          @signatureReset="endSession"
        />
      </v-col>
    </v-row>

    <v-dialog
      v-model="preview.show"
      max-width="650"
    >
      <v-card
        class="content__card"
        :style="{
          width: '100%',
          height: `${preview.sizes.height}px`
        }"
      >
        <pdf-viewer
          ref="pdfViewer"
          :source="preview.pdf"
          :page="preview.pages.current"
          @rendered="onPdfRendered"
        />
        <div
          v-if="preview.pages.total > 1"
          :key="preview.pages.total"
          class="my-4 content__pages"
          :class="{ 'content__pages--animate': preview.pages.total }"
          @mouseover="onPagesHover"
          @mouseleave="onPagesLeave"
        >
          <div
            ref="pagesBackground"
            class="content__pages__background"
          >
            <v-pagination
              v-model="preview.pages.current"
              total-visible="5"
              :color="appConfig.color.secondary"
              :length="preview.pages.total"
            />
          </div>
        </div>
        <ResizeObserver @notify="onDialogResize" />
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  asyncCallWithTimeout,
  isArray
} from '@/helpers/helpers'
import { VerificationResultSignature } from '@/helpers/mappers'
import ResultTable from '@/components/ResultTable'
import ResultFileList from '@/components/ResultFileList'
import { apiGetPreview, apiPostedDocumentsList, apiResetAllDocuments } from '@/api/api'
import { mapActions, mapState } from 'vuex'
import pdfViewer from 'vue-pdf-embed/dist/vue2-pdf-embed'
import { STEP } from '@/helpers/constants'
import { ResizeObserver } from 'vue-resize'
import { delay } from 'lodash'

export default {
  name: 'SignatureResultPage',
  components: { ResultFileList, ResultTable, pdfViewer, ResizeObserver },

  data () {
    return {
      mappedData: [],
      currentDocId: '',
      appConfig: this.$appConfig,
      appColor: this.$appConfig.color,
      docList: null,
      docListLoadingId: '',
      preview: {
        show: false,
        docId: null,
        pdf: null,
        pages: {
          total: 1,
          current: 1
        },
        sizes: {
          height: null,
          scale: 1.414
        }
      },
      panelStates: []
    }
  },

  computed: {
    ...mapState({
      resultData: state => state.signature.resultData
    }),

    currentDoc () {
      const doc = this.mappedData.find(item => item.id === this.currentDocId)
      if (!doc) {
        return { info: { main: [], signature: [], certificate: [], plugins: [] } }
      }
      return doc
    }
  },

  beforeMount () {
    if (this.resultData && typeof this.resultData === 'object') {
      const mappedResult = new VerificationResultSignature(this.resultData)

      if (isArray(mappedResult)) {
        this.mappedData = mappedResult
        this.currentDocId = this.mappedData[0].id

        delay(this.openPanels, 300)
      } else {
        this.mappedData = []
      }
    }
  },

  methods: {
    ...mapActions('notification', ['showAlert']),
    ...mapActions(['changeStep', 'resultDataSave', 'resetStateByType']),

    onDocClick (id) {
      this.currentDocId = id

      this.openPanels()
    },

    async onShowPreview (doc) {
      if (!this.docList) {
        this.docList = await apiPostedDocumentsList()
      }

      if (!this.docList.length) {
        this.showAlert('Сессия устарела, просмотр недоступен')
        return
      }

      try {
        const docInfo = this.mappedData.find(data => doc.id === data.id)
        const customId = docInfo?.id
        const finalTitle = docInfo.titleDoc ?? docInfo.titleMain
        const realId = this.docList.find(doc => doc.Name === finalTitle)?.id

        if (!realId) {
          this.showAlert('Просмотр документа недоступен')
          return
        }

        const previewResult = await this.getPreview(realId, customId)
        if (previewResult === 'ok') {
          this.preview.show = true
        }
      } catch (e) {
        this.showAlert('Просмотр документа недоступен')
        console.error('onShowPreview', e)
      }
    },

    async getPreview (docId, customId) {
      if (docId === this.preview.docId) {
        this.preview.pages.total = this.$refs.pdfViewer?.pageCount ?? 1
        return 'ok'
      }

      this.docListLoadingId = customId

      try {
        const pdfPreview = await asyncCallWithTimeout(
          apiGetPreview(docId),
          this.appConfig.previewLoadTimeout,
          'Загрузка превью (результаты)'
        )

        if (pdfPreview?.type === 'application/json') {
          this.preview.pages.total = 0
          this.showAlert('Не удалось отобразить документ')
          return 'error'
        }

        this.preview.pdf = URL.createObjectURL(pdfPreview)
        this.preview.docId = docId
        this.preview.pages.total = this.$refs.pdfViewer?.pageCount ?? 1
        return 'ok'
      } catch (error) {
        this.preview.pages.total = 0
        this.showAlert('Не удалось отобразить документ')
        return 'error'
      } finally {
        this.preview.pages.current = 1
        this.docListLoadingId = ''
      }
    },

    onPdfRendered () {
      // Определяем количество страниц
      this.preview.pages.total = this.$refs.pdfViewer?.pageCount ?? 1
      this.preview.sizes.height = this.$refs.pdfViewer?.$el?.clientHeight
      this.preview.sizes.scale = this.$refs.pdfViewer?.$el?.clientHeight / this.$refs.pdfViewer?.$el?.clientWidth
    },

    onDialogResize () {
      if (this.$refs.pdfViewer?.$el) {
        this.preview.sizes.height = this.$refs.pdfViewer.$el.clientWidth * this.preview.sizes.scale
      }

      try {
        if (
          this.$refs.pdfViewer?.$el.children?.length &&
          this.$refs.pdfViewer.$el.children[0].children?.length &&
          this.$refs.pdfViewer.$el.children[0].children[0].children?.length &&
          this.$refs.pdfViewer.$el.children[0].children[0].children[0]?.tagName === 'CANVAS'
        ) {
          const canvas = this.$refs.pdfViewer.$el.children[0].children[0].children[0]

          canvas.style.width = '100%'
          canvas.style.height = '100%'
        }
      } catch (e) {
        console.warn(e)
      }
    },

    onPagesHover () {
      this.$refs.pagesBackground.style.backgroundColor = this.appConfig.color.mainLight
      this.$refs.pagesBackground.style.opacity = '1'
    },
    onPagesLeave () {
      this.$refs.pagesBackground.style.backgroundColor = 'transparent'
      this.$refs.pagesBackground.style.opacity = '0.5'
    },

    async goBack () {
      this.resultDataSave({ type: 'signature', data: null })
      this.changeStep({ type: 'signature', step: STEP.PREVIEW })
      this.$destroy()
    },

    async endSession () {
      const reset = await apiResetAllDocuments()
      this.resetStateByType('signature')
      if (!reset) {
        this.$router.go(0)
      }
      this.$destroy()
    },

    openPanels () {
      const signatureInfo = this.currentDoc.signatureResults

      try {
        for (const [sigIndex, sigInfo] of signatureInfo.entries()) {
          const panelsIndexes = []
          let indexToAdd = 0

          for (const infoKey in sigInfo.info) {
            if (infoKey !== 'additionalCertificates') {
              if (infoKey === 'plugins' && sigInfo.info.plugins?.length) {
                // eslint-disable-next-line no-unused-vars
                for (const pluginKey in sigInfo.info.plugins) {
                  panelsIndexes.push(indexToAdd++)
                }
              } else {
                panelsIndexes.push(indexToAdd++)
              }
            } else {
              indexToAdd++
            }
          }

          this.panelStates.splice(sigIndex, 1, panelsIndexes)
        }
      } catch (e) {
        console.warn('Не удалось сформировать panelStates', e)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .content {
    padding: 20px 5vw;

    @media(max-width: $cp-laptop-breakpoint) {
      padding: 20px 0;
    }

    @media(max-width: $cp-mobile-breakpoint) {
      display: block;
    }

    &__title {
      @media(max-width: $cp-mobile-breakpoint) {
        font-size: 0.9rem;
      }
    }

    &__row {
      height: 100%;

      @media(max-width: $cp-mobile-breakpoint) {
        display: flex;
        flex-direction: column-reverse;
        flex-grow: initial;
        align-items: center;
      }
    }

    &__col-result {
      @media(max-width: $cp-mobile-breakpoint) {
        flex-basis: auto;
        flex-grow: 1;
      }

      &__card {
        background-color: transparent;
      }
    }

    &__signature-title {
      width: 100%;
      background-color: white;
      min-height: 60px;

      display: flex;
      align-items: center;
      justify-content: center;

      margin-bottom: 16px;
      border-radius: $default-border-radius;

      &__text {
        font-size: 1.2rem;
        text-align: center;
        font-weight: 500;

        @media(max-width: $cp-mobile-breakpoint) {
          font-size: 0.9rem;
        }
      }
    }

    &__col-doclist {
      @media(max-width: $cp-mobile-breakpoint) {
        flex-basis: 0;
        flex-grow: 0;
      }
    }

    &__card {
      position: relative;
      background-color: white;
      min-width: 450px;
      min-height: 600px;
      transition: height .5s ease;
      overflow: hidden;
    }

    &__pages {
      position: fixed;
      left: 50%;
      bottom: 0;
      transform: translateX(-50%);
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: transparent;
      height: 70px;
      opacity: 0;
      transition: opacity 0.5s ease-out;

      &__background {
        padding: 10px;
        border-radius: $default-border-radius;
        opacity: 0.5;
      }

      &--animate {
        opacity: 1;
      }
    }
  }
</style>
